import chroma from 'chroma-js';

// Convert S3 URLs to backend proxy URLs for images behind private buckets
export const resolveImageUrl = (url) => {
  if (!url) return null;
  // Local API paths or relative URLs — use as-is
  if (url.startsWith('/')) return url;
  // S3 URLs — proxy through backend
  if (url.includes('.amazonaws.com/') || url.includes('.s3.')) {
    return `/api/files/download?path=${encodeURIComponent(url)}`;
  }
  return url;
};

// Helper to adjust color brightness.
// Kept as-is: the login gradient (LoginLayout) depends on this exact ramp.
export const adjustColor = (color, amount) => {
  if (!color) return '#0A5151';
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

// ---------------------------------------------------------------------------
// Brand colour model
//
// A tenant configures exactly ONE colour — the Brand Color, persisted as
// `primaryColor`. Everything else is derived from it:
//
//   BRAND           the exact selected colour. The dominant tenant identity —
//                   sidebar, primary buttons, primary table headers.
//   BRAND SOFT      a solid, visibly light tint of the brand. Subtle panels,
//                   selected/light backgrounds, card accents.
//   BRAND HOVER     a MODEST darker step for hover/pressed on brand fills.
//                   An interaction state, never a second identity colour.
//   BRAND INK       the brand darkened only as far as needed to be legible AS
//                   text on white/paper. A no-op when the brand already is.
//                   Text, borders, focus rings, accent headings — not large
//                   filled surfaces.
//   BRAND CONTRAST  white or near-black, whichever is readable ON the brand.
//   BRAND TINT      low-alpha brand, for overlays that must sit on an
//                   arbitrary surface rather than replace it.
//
// The intended feel: Brand = identity, Soft = subtle background,
// Hover = interaction, Ink = readable accent/text, Contrast = content on Brand.
// Ordinary page and card surfaces stay neutral; status stays semantic.
//
// `secondaryColor` remains in the API/DB contract for backward compatibility
// and is written as BRAND INK on save, but tenant identity is never resolved
// from it at runtime — it is always derived from the Brand Color, so a legacy
// fixed-luminance companion cannot wash out the colour the tenant chose.
//
// Dark mode never uses tenant colours; it runs on a fixed platform palette
// (see TenantThemeProvider). Every helper below preserves that.
// ---------------------------------------------------------------------------

/**
 * The official Thaklis default brand colour — the single canonical value for
 * "this tenant has not chosen a colour" and for Reset to Default.
 *
 * Historical, not invented: it is the brand colour from the initial commit
 * (`colors.js` primary.main, the login gradient, the Tickets table header) and
 * it is still the server-side default in `TenantConfigDTO.ThemeConfig`, so an
 * unbranded tenant already renders this today. `#3CAEA3` replaced it in
 * `colors.js` only as part of the later Grafana-inspired dark-mode palette and
 * is the fixed DARK accent, not the light-mode default.
 *
 * Deliberately independent of BRAND_PRESETS ordering: which swatch appears
 * first is a presentation choice and must never define the default.
 */
export const DEFAULT_BRAND_COLOR = '#0E6B6B'; // Thaklis deep teal

/** Near-black used as the dark half of every contrast decision. Matches the
 *  dark palette's page background so brand fills read consistently. */
export const NEAR_BLACK = '#111217';

/** Target contrast for brand-coloured foreground on white/paper. */
export const BRAND_INK_MIN_CONTRAST_ON_WHITE = 4.5;

/** How far BRAND SOFT is mixed toward white. High enough that the tint reads
 *  as a light surface carrying ordinary dark text, low enough to stay clearly
 *  the tenant's hue rather than a neutral grey. */
const BRAND_SOFT_MIX = 0.88;

export const isValidHex = (hex) =>
  typeof hex === 'string' && /^#[0-9A-Fa-f]{6}$/.test(hex.trim());

/**
 * True when the colour is so pale that a filled surface loses its edge against a
 * white card. Callers use this to add a derived outline.
 */
export const isBrandColorPale = (hex) =>
  isValidHex(hex) && chroma.contrast(hex.trim(), '#ffffff') < 1.6;

/**
 * Readable text colour for anything painted on top of `hex`.
 * Picks whichever of white / near-black has the better contrast.
 */
export const getContrastText = (hex) => {
  if (!isValidHex(hex)) return '#ffffff';
  return chroma.contrast(hex, '#ffffff') >= chroma.contrast(hex, NEAR_BLACK)
    ? '#ffffff'
    : NEAR_BLACK;
};

// ---------------------------------------------------------------------------
// Pure derivations — take a hex, return a hex. The Branding preview calls these
// with the unsaved form value so it renders exactly what the app will.
// ---------------------------------------------------------------------------

/**
 * Nudge `hex` until it reads at `minRatio` against `background`, moving away
 * from the background (darker on a light ground, lighter on a dark one) in
 * small Lab-space steps so the hue survives. Returns the colour untouched when
 * it already clears the bar — this is a floor, never a fixed luminance target.
 *
 * Used for BRAND INK and for making a caller-supplied semantic value legible on
 * whatever surface it lands on, without changing what that value means.
 */
export const ensureReadableOn = (hex, background = '#ffffff', minRatio = 4.5) => {
  if (!isValidHex(hex)) return hex;
  const backgroundIsDark = chroma(background).luminance() < 0.5;
  let c = chroma(hex.trim());
  let guard = 0;
  while (chroma.contrast(c, background) < minRatio && guard < 80) {
    c = backgroundIsDark ? c.brighten(0.1) : c.darken(0.1);
    guard += 1;
  }
  return c.hex();
};

/**
 * BRAND INK — the brand colour made legible as foreground on white.
 *
 * For TEXT, BORDERS and FOCUS only. Ink is deliberately not a second identity
 * colour: filling large surfaces with it is what made the UI feel heavy, so
 * large branded surfaces take BRAND itself and soft surfaces take BRAND SOFT.
 */
export const deriveBrandInk = (hex) => {
  if (!isValidHex(hex)) return DEFAULT_BRAND_COLOR;
  return ensureReadableOn(hex, '#ffffff', BRAND_INK_MIN_CONTRAST_ON_WHITE);
};

/**
 * BRAND SOFT — a solid, visibly light tint of the brand.
 *
 * Mixed toward white in Lab space rather than produced with alpha, so it is
 * stable over any surface and stays recognisably the brand's hue. This is the
 * surface for subtle branded panels, selected rows and card accents; ordinary
 * dark text remains readable on it.
 */
export const deriveBrandSoft = (hex) => {
  if (!isValidHex(hex)) return '#f5f5f5';
  return chroma.mix(hex.trim(), '#ffffff', BRAND_SOFT_MIX, 'lab').hex();
};

/**
 * BRAND HOVER — the pressed/hover state for a filled brand surface.
 *
 * A modest step only. It is an interaction state, not a second brand colour,
 * so it must stay close enough to BRAND that the two read as one identity.
 */
export const deriveBrandHover = (hex) => {
  if (!isValidHex(hex)) return DEFAULT_BRAND_COLOR;
  const value = hex.trim();
  const step = getContrastText(value) === '#ffffff' ? 0.25 : 0.18;
  return chroma(value).darken(step).hex();
};

/** BRAND GRADIENT endpoint — a slightly deeper stop in the same hue family. */
export const deriveBrandGradientEnd = (hex) => {
  if (!isValidHex(hex)) return DEFAULT_BRAND_COLOR;
  return chroma(hex.trim()).darken(0.5).hex();
};

/**
 * Hex to the `H S% L%` triplet shadcn CSS variables expect. Shared so the
 * theme provider and the shadcn DataTable emit identical values.
 */
export const hexToHslTriplet = (hex) => {
  if (!isValidHex(hex)) return '222.2 47.4% 11.2%';
  const [h, s, l] = chroma(hex.trim()).hsl();
  const hue = Number.isNaN(h) ? 0 : h;
  return `${Math.round(hue)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

/**
 * Generic alpha overlay. Used for surfaces that sit ON a coloured ground and
 * must follow whatever text colour that ground resolved to — sidebar hover and
 * active rows, dividers, muted captions.
 */
export const withAlpha = (hex, opacity) => {
  if (!isValidHex(hex)) return `rgba(255,255,255,${opacity})`;
  return chroma(hex.trim()).alpha(opacity).css();
};

/** Low-alpha brand for soft surfaces. Returns a CSS rgba() string. */
export const deriveBrandTint = (hex, opacity = 0.08) => {
  if (!isValidHex(hex)) return `rgba(0,0,0,${opacity})`;
  return withAlpha(hex, opacity);
};

/**
 * The value written to the persisted `secondaryColor` field. Kept so the
 * stored pair stays meaningful for any consumer reading it directly (invoice
 * PDFs, a future mobile client) without it ever driving tenant identity here.
 */
export const deriveStoredCompanion = (hex) => deriveBrandInk(hex);

// ---------------------------------------------------------------------------
// Platform identity — fixed, never tenant-driven. Used by the platform-admin
// and group-owner shells.
// ---------------------------------------------------------------------------

export const PLATFORM_BRAND_COLOR = '#064d51';
export const PLATFORM_BRAND_DARK = '#053a3d';

// ---------------------------------------------------------------------------
// Theme-aware accessors. Each one preserves the controlled dark-mode behaviour
// its predecessor had.
// ---------------------------------------------------------------------------

/**
 * BRAND — the exact colour the tenant selected. `palette.primary.main` holds
 * it in tenant light mode and the fixed dark accent in dark mode, so reading
 * from the palette keeps dark mode controlled automatically.
 */
export const getBrandColor = (theme, scope = 'tenant') => {
  if (scope === 'platform') return PLATFORM_BRAND_COLOR;
  return theme?.palette?.primary?.main || DEFAULT_BRAND_COLOR;
};

/** Readable text/icon colour on top of BRAND. */
export const getBrandContrastText = (theme, scope = 'tenant') => {
  if (scope === 'platform') return '#ffffff';
  return theme?.palette?.primary?.contrastText || getContrastText(getBrandColor(theme, scope));
};

/** BRAND INK — brand-coloured foreground on white/paper. */
export const getBrandInk = (theme, scope = 'tenant') => {
  if (scope === 'platform') return PLATFORM_BRAND_COLOR;
  // Dark mode already runs a fixed, deliberately legible accent — leave it be.
  if (theme?.palette?.mode === 'dark') return getBrandColor(theme, scope);
  return deriveBrandInk(getBrandColor(theme, scope));
};

/** BRAND HOVER — pressed/hover state for a filled brand surface. */
export const getBrandHover = (theme, scope = 'tenant') => {
  if (scope === 'platform') return PLATFORM_BRAND_DARK;
  return deriveBrandHover(getBrandColor(theme, scope));
};

/**
 * BRAND SOFT — a solid light branded surface. Dark mode keeps its own
 * controlled elevated surface rather than tinting toward the tenant colour.
 */
export const getBrandSoft = (theme, scope = 'tenant') => {
  if (theme?.palette?.mode === 'dark') return '#22252b';
  return deriveBrandSoft(getBrandColor(theme, scope));
};

export const getBrandTint = (theme, opacity = 0.08) => {
  if (theme?.palette?.mode === 'dark') {
    return `rgba(255,255,255,${Math.max(opacity * 0.75, 0.03)})`;
  }
  return deriveBrandTint(getBrandColor(theme), opacity);
};

export const getBrandHoverTint = (theme, opacity = 0.14) => {
  if (theme?.palette?.mode === 'dark') {
    return `rgba(255,255,255,${Math.max(opacity * 0.75, 0.06)})`;
  }
  return deriveBrandTint(getBrandColor(theme), opacity);
};

export const getBrandGradient = (theme, scope = 'tenant') => {
  if (theme?.palette?.mode === 'dark') {
    return 'linear-gradient(135deg, #1a2e2e 0%, #0d1f1f 100%)';
  }
  const base = getBrandColor(theme, scope);
  const end = scope === 'platform'
    ? PLATFORM_BRAND_DARK
    : deriveBrandGradientEnd(base);
  return `linear-gradient(135deg, ${base} 0%, ${end} 100%)`;
};

/**
 * Shared header style for CATEGORY A primary tenant data tables — the main
 * list a page exists to show. Embedded, report and platform tables stay
 * neutral and must not use this.
 *
 * Applied once on <TableHead>, never per cell.
 */
export const getBrandedTableHeadSx = (theme, scope = 'tenant') => {
  const isDark = theme?.palette?.mode === 'dark';
  const background = isDark
    ? 'grey.800'
    : getBrandColor(theme, scope);
  const color = isDark
    ? theme?.palette?.text?.primary || '#e9edf2'
    : getBrandContrastText(theme, scope);

  return {
    bgcolor: background,
    '& .MuiTableCell-head': {
      // Repeated on the cell as well as the row: a `stickyHeader` table paints
      // its own cell background, which would otherwise cover the head's.
      backgroundColor: background,
      color,
      fontWeight: 600,
      '&:first-of-type': { borderTopLeftRadius: 8 },
      '&:last-of-type': { borderTopRightRadius: 8 },
    }
  };
};
